import type { ValidatorsResponse } from './api.ts';

export interface ValidationRule {
  name: string;
  type?: string;
  expression?: string;
  message: string;
}

interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export const validationRules: Record<string, (value: string, param?: unknown) => boolean> = {
  required: (value: string, param?: unknown): boolean => {
    const el = param as HTMLInputElement;
    if (el.type === 'checkbox' || el.type === 'radio') {
      const name = el.getAttribute('name');
      if (name && el.form) {
        const group = el.form.querySelectorAll(`input[name="${name}"]`);
        if (group.length > 1) {
          return Array.from(group).some((input) => (input as HTMLInputElement).checked);
        }
      }
      return el.checked;
    }

    return !!value && value.trim().length > 0;
  },
  RegexValidator: (value: string, param?: unknown): boolean => {
    if (!value) return true;
    return new RegExp(param as string).test(value);
  },
  noHTML: (value: string): boolean => {
    if (!value) return true;
    return !new RegExp('<[^>]*>').test(value);
  },
};

export function getValidatorRegex(
  validationName: string,
  validators: ValidatorsResponse | undefined,
): string {
  const matchedRule = validators?.nodes?.find((el) => el.name === validationName);

  if (!matchedRule) {
    console.warn(`Validator not found for ${validationName}`);
    return '.*';
  }

  const expression = matchedRule.properties.find((el) => el.name === 'expression');

  if (!expression || !expression.values.length) return '.*';

  try {
    new RegExp(expression.values[0]);

    return expression.values[0];
  } catch {
    console.warn(`Invalid regex for ${validationName}`);

    return '.*';
  }
}

export function validate(el: HTMLInputElement, rules: ValidationRule[]): ValidationResult {
  const value = el.value;

  for (const ruleConfig of rules) {
    const { name, expression, message } = ruleConfig;
    const rule = validationRules[name] || validationRules[ruleConfig.type as string];

    if (rule) {
      const isValid =
        ruleConfig.type === 'RegexValidator' ? rule(value, expression) : rule(value, el);
      if (!isValid) {
        return { isValid: false, message };
      }
    }
  }

  return { isValid: true };
}
